import { FC, useMemo, useState, useEffect } from 'react'

import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import { usePageViewEvent } from 'src/Hooks/Analytics/useTrackEvents'
import { getValueOrEmptyString } from 'src/Helpers/layoutHelpers'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { ChosenTrack } from 'src/Components/ChosenTrack'
import { AdsFAQ } from 'src/Components/AdsFAQ'
import { useSearchSpotifyTrack } from 'src/Redux/spotifyTracksApi/useSearchSpotifyTrack'
import {
  enteredTrackUrlEvent,
  startedEnterTrackEvent,
} from 'src/Helpers/TagManager'
import { useClearSpotifyTracksMutation } from 'src/Redux/spotifyTracksApi'
import { DEBOUNCE_TIMER_1000 } from 'src/Constants/constants'
import { PublicRoutesEnum } from 'src/Router/PublicComponentsNewUI'

import {
  SearchedTrack,
  SearchTrackFormData,
} from '../../../../Components/TrackSearch'
import { mapSpotifyTrack } from '../../../../helper'
import { TrackSearchError } from '../../../../Components/TrackSearchError'
import { DesktopTabletTrackSearch } from '../../../../Components/TrackSearch/Components/DesktopTabletTrackSearch'
import { MobileTrackSearch } from '../../../../Components/TrackSearch/Components/MobileTrackSearch'

const Container = styled.div`
  display: flex;
  margin: 0 auto;
  width: 100%;
  max-width: 960px;
  padding: 24px;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;

  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
`
const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 32px;
  color: ${({ theme }) => theme.color.darkGrey};
`

export const SearchTrackFormNoAuth: FC = () => {
  const { trackAddedEventHandler } = usePageViewEvent()
  const navigate = useNavigate()
  const [track, setTrack] = useState<SearchedTrack | null>(null)
  const {
    searchTracks,
    tracks,
    loading: isLoadingSpotify,
    error,
  } = useSearchSpotifyTrack()
  const [clearSpotifyTracks] = useClearSpotifyTracksMutation()

  useEffect(() => {
    if (track?.id) {
      trackAddedEventHandler({
        artistName: getValueOrEmptyString(track.artistName),
        platformType: CampaignPlatformTypeEnum.META,
        trackDuration: track.trackDuration,
        trackId: getValueOrEmptyString(track.id),
        trackName: getValueOrEmptyString(track.name),
      })
    }
  }, [track, trackAddedEventHandler])

  const { control, reset } = useForm<SearchTrackFormData>({
    mode: 'onChange',
  })

  const formattedTracks = useMemo<SearchedTrack[]>(
    () => tracks?.map(mapSpotifyTrack) || [],
    [tracks],
  )

  const debouncedFindTrack = useDebouncedCallback((searchInput: string) => {
    searchTracks(searchInput)

    const eventsParams = {
      campaignPlatform: CampaignPlatformTypeEnum.META,
      userId: null,
      isFirstCampaign: true,
      campaignNumber: 1,
    }
    if (searchInput.includes('https://')) {
      enteredTrackUrlEvent(eventsParams)
    } else {
      startedEnterTrackEvent(eventsParams)
    }
  }, DEBOUNCE_TIMER_1000)

  const onOutsideClick = (): void => {
    clearSpotifyTracks()
  }

  const onSearch = (searchInput: string): void => {
    if (searchInput === '') {
      debouncedFindTrack.cancel()
      clearSpotifyTracks()
    } else {
      debouncedFindTrack(searchInput)
    }
  }

  const onItemClick = (_: string, item?: SearchedTrack): void => {
    if (item) {
      setTrack(item)
    }
  }

  const handleCreate = (): void => {
    if (track) {
      navigate(
        `${PublicRoutesEnum.CreateCampaign}/ads/track/${track.id}${PublicRoutesEnum.SignUpArtist}`,
      )
    }
  }

  const onClickCreate = (): void => {
    reset()
    setTrack(null)
  }

  const searchError = useMemo(() => {
    if (error) {
      return <TrackSearchError>{error}</TrackSearchError>
    }
    return null
  }, [error])

  if (track) {
    return (
      <ChosenTrack
        artist={track.artistName}
        title={'New Meta Ads Campaign'}
        duration={track.trackDuration}
        trackName={track.name}
        trackUrl={track.previewUrl}
        image={track.trackCoverUrl}
        onChangeClick={onClickCreate}
        onCreateClick={handleCreate}
      />
    )
  }

  return (
    <>
      <Container>
        <Title>New Meta Ads Campaign</Title>
        <DesktopTabletTrackSearch
          isLoading={isLoadingSpotify}
          onItemClick={onItemClick}
          tracks={formattedTracks}
          onSearch={onSearch}
          onOutsideClick={onOutsideClick}
          placeholder={'Type track name or URL from Spotify'}
          control={control}
          error={searchError}
        />

        <MobileTrackSearch
          isLoading={isLoadingSpotify}
          onItemClick={onItemClick}
          tracks={formattedTracks}
          onSearch={onSearch}
          placeholder={'Type your track name or Spotify link'}
          control={control}
          error={searchError}
        />
      </Container>
      <AdsFAQ />
    </>
  )
}
